import React, { useState, useEffect } from 'react';
import { Box, useToast, Spinner, Center } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import SocialActions from '../components/shorts/SocialActions';
import ChannelInfo from '../components/shorts/ChannelInfo';
import VideoDescription from '../components/shorts/VideoDescription';
import ShortVideoPlayer from '../components/shorts/ShortVideoPlayer';
import { getContentById } from '../services/contentService';
import { getUserProfile } from '../services/userService';
import { getPublisherSubscribers, isSubscribedToPublisher, subscribeToPublisher, unsubscribeFromPublisher, likeContent, unlikeContent } from '../services/subscriptionService';
import { shareContent } from '../services/shareService';
import { useAuth } from '../contexts/AuthContext';

interface ShortPlayerData {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  posterUrl?: string;
  uploader: string;
  uploaderId: string;
  uploaderAvatar?: string;
  subscriberCount: number;
  likes: number;
  dislikes: number;
  views: number;
}

const ShortPlayer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  
  const [shortData, setShortData] = useState<ShortPlayerData | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => {
    if (id) {
      loadShortData();
    }
  }, [id]);

  const loadShortData = async () => {
    try {
      setIsLoading(true);
      const content = await getContentById(id!);
      
      if (!content || content.category !== 'short') {
        toast({
          title: 'Short not found',
          description: 'This short video could not be found.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        navigate('/');
        return;
      }

      // Fetch publisher profile for real avatar and subscriber count
      let publisherProfile = null;
      let subscriberCount = 0;
      try {
        publisherProfile = await getUserProfile(content.uploaderId);
        const subscribers = await getPublisherSubscribers(content.uploaderId);
        subscriberCount = subscribers.length;
      } catch (error) {
        console.error('Error fetching publisher profile:', error);
      }

      // Use content likes/dislikes from the content itself
      const likesData = { 
        likes: content.likes || 0, 
        dislikes: content.dislikes || 0 
      };

      // Check if user is subscribed
      let subscribed = false;
      if (user) {
        try {
          subscribed = await isSubscribedToPublisher(content.uploaderId, user.uid);
        } catch (error) {
          console.error('Error checking subscription:', error);
        }
      }

      // Find the video file URL from assets
      let videoUrl = '';
      if (content.assets) {
        // First, check for common video asset keys
        const videoKeys = ['video', 'videoFile', 'videoUrl', 'file', 'fileUrl'];
        for (const key of videoKeys) {
          if (content.assets[key] && typeof content.assets[key] === 'string') {
            videoUrl = content.assets[key] as string;
            break;
          }
        }
        
        // If not found, look for video files in assets (stored by filename)
        if (!videoUrl) {
          for (const [key, value] of Object.entries(content.assets)) {
            if (typeof value === 'string' && 
                (key.toLowerCase().includes('video') ||
                 key.toLowerCase().includes('.mp4') || 
                 key.toLowerCase().includes('.mov') || 
                 key.toLowerCase().includes('.avi') ||
                 key.toLowerCase().includes('.webm'))) {
              videoUrl = value;
              break;
            }
          }
        }
        
        // Also check if assets has a nested structure
        if (!videoUrl && typeof content.assets === 'object') {
          const assetValues = Object.values(content.assets);
          for (const value of assetValues) {
            if (typeof value === 'string' && 
                (value.includes('firebasestorage') || 
                 value.includes('.mp4') || 
                 value.includes('.mov') ||
                 value.includes('.webm'))) {
              videoUrl = value;
              break;
            }
          }
        }
      }
      
      // If still no video URL, check if content has a direct videoUrl property
      if (!videoUrl && (content as any).videoUrl) {
        videoUrl = (content as any).videoUrl;
      }

      setShortData({
        id: content.id,
        title: content.title,
        description: content.description,
        videoUrl: videoUrl,
        posterUrl: content.poster || content.thumbnail,
        uploader: content.uploader,
        uploaderId: content.uploaderId,
        uploaderAvatar: publisherProfile?.avatarUrl || content.uploaderAvatar,
        subscriberCount: subscriberCount,
        likes: likesData.likes,
        dislikes: likesData.dislikes,
        views: content.views || 0
      });

      setLikes(likesData.likes);
      setDislikes(likesData.dislikes);
      setIsSubscribed(subscribed);
      
      // Initialize like/dislike states - for now, assume user hasn't interacted
      // In a real app, you'd check if the user has previously liked/disliked this content
      setIsLiked(false);
      setIsDisliked(false);
    } catch (error) {
      console.error('Error loading short:', error);
      toast({
        title: 'Error',
        description: 'Failed to load short video.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };


  const handleLike = async () => {
    if (!user || !shortData || isInteracting) return;
    
    setIsInteracting(true);
    
    try {
      if (isLiked) {
        // Unlike - remove the like
        await unlikeContent(shortData.id, user.uid);
        setLikes(prev => Math.max(0, prev - 1));
        setIsLiked(false);
      } else {
        // Like - add the like
        await likeContent(shortData.id, user.uid);
        setLikes(prev => prev + 1);
        setIsLiked(true);
        
        // If user was previously disliking, remove the dislike
        if (isDisliked) {
          setDislikes(prev => Math.max(0, prev - 1));
          setIsDisliked(false);
        }
      }
    } catch (error) {
      console.error('Error handling like:', error);
      toast({
        title: 'Error',
        description: 'Failed to update like status.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsInteracting(false);
    }
  };

  const handleDislike = async () => {
    if (!user || !shortData || isInteracting) return;
    
    setIsInteracting(true);
    
    try {
      if (isDisliked) {
        // Remove dislike - just update local state since we don't have a separate dislike service
        setDislikes(prev => Math.max(0, prev - 1));
        setIsDisliked(false);
      } else {
        // Add dislike - just update local state since we don't have a separate dislike service
        setDislikes(prev => prev + 1);
        setIsDisliked(true);
        
        // If user was previously liking, remove the like
        if (isLiked) {
          await unlikeContent(shortData.id, user.uid);
          setLikes(prev => Math.max(0, prev - 1));
          setIsLiked(false);
        }
      }
    } catch (error) {
      console.error('Error handling dislike:', error);
      toast({
        title: 'Error',
        description: 'Failed to update dislike status.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsInteracting(false);
    }
  };

  const handleShare = async () => {
    if (!shortData || !user) return;
    
    try {
      const shareUrl = `${window.location.origin}/short/${id}`;
      await shareContent(user.uid, shortData.id, 'short', 'link');
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Shared!',
        description: 'Link copied to clipboard.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to share:', error);
      toast({
        title: 'Error',
        description: 'Failed to share content.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleDonate = () => {
    if (shortData) {
      navigate(`/donation/${shortData.uploaderId}/${shortData.id}`, {
        state: { creatorName: shortData.uploader }
      });
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to subscribe to creators.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!shortData) {
      toast({
        title: 'Error',
        description: 'Content data not available.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    
    try {
      setIsInteracting(true);
      if (isSubscribed) {
        // Unsubscribe
        await unsubscribeFromPublisher(shortData.uploaderId, user.uid);
        setIsSubscribed(false);
        // Update subscriber count
        setShortData(prev => prev ? { ...prev, subscriberCount: Math.max(0, prev.subscriberCount - 1) } : null);
        toast({
          title: 'Unsubscribed',
          description: `You unsubscribed from ${shortData.uploader}`,
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      } else {
        // Subscribe
        await subscribeToPublisher(shortData.uploaderId, user.uid);
        setIsSubscribed(true);
        // Update subscriber count
        setShortData(prev => prev ? { ...prev, subscriberCount: prev.subscriberCount + 1 } : null);
        toast({
          title: 'Subscribed!',
          description: `You subscribed to ${shortData.uploader}`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error handling subscription:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Check for specific Firebase errors
      let userFriendlyMessage = errorMessage;
      if (errorMessage.includes('permission') || errorMessage.includes('Permission')) {
        userFriendlyMessage = 'You do not have permission to perform this action.';
      } else if (errorMessage.includes('not-found') || errorMessage.includes('not found')) {
        userFriendlyMessage = 'Creator profile not found.';
      } else if (errorMessage.includes('network') || errorMessage.includes('Network')) {
        userFriendlyMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast({
        title: 'Error',
        description: `Failed to update subscription: ${userFriendlyMessage}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsInteracting(false);
    }
  };

  const handleVideoEnd = () => {
    // TODO: Implement next video logic
  };

  const handleVideoError = (error: string) => {
    console.error('Video error:', error);
  };

  if (isLoading || !shortData) {
    return (
      <Center w="100vw" h="100vh" bg="black">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  return (
    <Box
      w="100vw"
      h="100vh"
      bg="black"
      position="relative"
      overflow="hidden"
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      pt="2vh"
    >
      {/* Central Video Container - TikTok-style 9:16 aspect ratio */}
      <Box
        position="relative"
        w="100%"
        h="100%"
        maxW={{ base: "40vh", md: "50vh" }}
        maxH={{ base: "75vh", md: "85vh" }}
        aspectRatio="9/16"
        bg="black"
        overflow="hidden"
      >
        <ShortVideoPlayer
          videoUrl={shortData.videoUrl}
          posterUrl={shortData.posterUrl}
          onVideoEnd={handleVideoEnd}
          onVideoError={handleVideoError}
          autoPlay
          loop
          muted={false}
        />


        {/* Right Sidebar - TikTok-style interaction panel */}
        <SocialActions
          likes={likes}
          dislikes={dislikes}
          isLiked={isLiked}
          isDisliked={isDisliked}
          onLike={handleLike}
          onDislike={handleDislike}
          onShare={handleShare}
          onDonate={handleDonate}
          creatorName={shortData.uploader}
          creatorId={shortData.uploaderId}
          contentId={shortData.id}
          contentTitle={shortData.title}
        />

        {/* Bottom Left - Channel Info and Description */}
        <ChannelInfo
          channelName={shortData.uploader}
          subscriberCount={shortData.subscriberCount}
          avatarUrl={shortData.uploaderAvatar}
          isSubscribed={isSubscribed}
          onSubscribe={handleSubscribe}
          isPublisher={user?.uid === shortData.uploaderId}
          onEdit={() => navigate(`/short/${shortData.id}/edit`)}
        />

        <VideoDescription
          title={shortData.title}
          description={shortData.description}
        />

      </Box>
    </Box>
  );
};

export default ShortPlayer;
